import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, Search, Upload, Image as ImageIcon, Film, Music, Volume2, 
  UserCircle2, Check, Grid3X3, List, Star, RefreshCw, Plus,
  ChevronDown, Filter, FolderOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useAuthStore } from '../lib/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Media type configuration
const MEDIA_TYPES = {
  all: { icon: FolderOpen, label: 'All', color: 'text-white' },
  video: { icon: Film, label: 'Videos', color: 'text-purple-500' },
  audio: { icon: Volume2, label: 'Audio', color: 'text-blue-500' },
  music: { icon: Music, label: 'Music', color: 'text-pink-500' },
  image: { icon: ImageIcon, label: 'Images', color: 'text-green-500' },
  voice_clone: { icon: UserCircle2, label: 'Voice Clones', color: 'text-orange-500' }
};

// Format duration
const formatDuration = (seconds) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * MediaPickerModal - A reusable component for selecting media assets from the library
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when asset(s) are selected, receives array of assets
 * @param {string} mediaType - Filter by type: 'all', 'video', 'audio', 'music', 'image', 'voice_clone'
 * @param {boolean} multiple - Allow multiple selection
 * @param {string} title - Modal title
 * @param {boolean} allowUpload - Show upload tab
 * @param {string} uploadAccept - File types to accept for upload (e.g., 'image/*')
 * @param {function} onUpload - Callback when file is uploaded, receives File object
 */
export default function MediaPickerModal({
  isOpen,
  onClose,
  onSelect,
  mediaType = 'all',
  multiple = false,
  title = 'Select Media',
  allowUpload = true,
  uploadAccept = '*/*',
  onUpload = null
}) {
  const { token } = useAuthStore();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('library'); // 'library' or 'upload'
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(mediaType === 'all' ? null : mediaType);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Fetch assets from Media Library
  const fetchAssets = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (selectedType) params.append('media_type', selectedType);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '100');
      
      const response = await fetch(`${API_URL}/api/media?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch assets');
      
      const data = await response.json();
      setAssets(data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  }, [token, selectedType, searchQuery]);
  
  // Fetch assets when modal opens or filters change
  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      setSelectedAssets([]);
    }
  }, [isOpen, selectedType, fetchAssets]);
  
  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) fetchAssets();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Handle asset selection
  const handleAssetClick = (asset) => {
    if (multiple) {
      setSelectedAssets(prev => {
        const isSelected = prev.some(a => a.id === asset.id);
        if (isSelected) {
          return prev.filter(a => a.id !== asset.id);
        } else {
          return [...prev, asset];
        }
      });
    } else {
      setSelectedAssets([asset]);
    }
  };
  
  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedAssets.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    onSelect(multiple ? selectedAssets : selectedAssets[0]);
    onClose();
  };
  
  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    if (onUpload) {
      setUploading(true);
      try {
        await onUpload(file);
        toast.success('File uploaded successfully');
        // Refresh assets after upload
        setTimeout(() => fetchAssets(), 1000);
        setActiveTab('library');
      } catch (error) {
        toast.error('Upload failed: ' + error.message);
      } finally {
        setUploading(false);
      }
    } else {
      // Default: just pass the file back
      onSelect({ file, isUpload: true });
      onClose();
    }
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };
  
  if (!isOpen) return null;
  
  const isAssetSelected = (asset) => selectedAssets.some(a => a.id === asset.id);
  
  // Determine which types to show based on mediaType prop
  const availableTypes = mediaType === 'all' 
    ? ['all', 'video', 'audio', 'music', 'image', 'voice_clone']
    : ['all', mediaType];
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button 
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('library')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'library' 
                ? 'text-white border-b-2 border-purple-500' 
                : 'text-zinc-400 hover:text-white'
            }`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            From Library
          </button>
          {allowUpload && (
            <button
              onClick={() => setActiveTab('upload')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'upload' 
                  ? 'text-white border-b-2 border-purple-500' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Upload className="w-4 h-4 inline mr-2" />
              Upload New
            </button>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {activeTab === 'library' ? (
            <>
              {/* Filters */}
              <div className="p-4 border-b border-zinc-800 space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search media..."
                      className="pl-10 bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                  <div className="flex border border-zinc-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400'}`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Type filters */}
                {availableTypes.length > 2 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {availableTypes.map(type => {
                      const config = MEDIA_TYPES[type];
                      const Icon = config.icon;
                      const isActive = type === 'all' ? !selectedType : selectedType === type;
                      
                      return (
                        <button
                          key={type}
                          onClick={() => setSelectedType(type === 'all' ? null : type)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${
                            isActive 
                              ? 'bg-zinc-700 text-white' 
                              : 'text-zinc-400 hover:text-white'
                          }`}
                        >
                          <Icon className={`w-3.5 h-3.5 ${isActive ? config.color : ''}`} />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Assets Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 text-purple-500 animate-spin" />
                  </div>
                ) : assets.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm">No media found</p>
                    <p className="text-zinc-600 text-xs mt-1">
                      {searchQuery ? 'Try a different search' : 'Upload some files to get started'}
                    </p>
                  </div>
                ) : (
                  <div className={viewMode === 'grid' 
                    ? 'grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3'
                    : 'flex flex-col gap-2'
                  }>
                    {assets.map(asset => {
                      const typeConfig = MEDIA_TYPES[asset.media_type] || MEDIA_TYPES.image;
                      const Icon = typeConfig.icon;
                      const selected = isAssetSelected(asset);
                      
                      if (viewMode === 'list') {
                        return (
                          <div
                            key={asset.id}
                            onClick={() => handleAssetClick(asset)}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                              selected 
                                ? 'bg-purple-500/20 border border-purple-500' 
                                : 'bg-zinc-800/50 border border-transparent hover:border-zinc-700'
                            }`}
                          >
                            <div className={`w-12 h-12 rounded-lg ${typeConfig.color} bg-opacity-10 flex items-center justify-center overflow-hidden flex-shrink-0`}>
                              {asset.thumbnail_url ? (
                                <img src={asset.thumbnail_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Icon className={`w-5 h-5 ${typeConfig.color}`} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm truncate">{asset.name}</p>
                              <p className="text-zinc-500 text-xs">
                                {typeConfig.label}
                                {asset.duration && ` • ${formatDuration(asset.duration)}`}
                              </p>
                            </div>
                            {selected && (
                              <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return (
                        <div
                          key={asset.id}
                          onClick={() => handleAssetClick(asset)}
                          className={`relative aspect-square rounded-lg cursor-pointer overflow-hidden group transition-all ${
                            selected 
                              ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-zinc-900' 
                              : 'hover:ring-1 hover:ring-zinc-600'
                          }`}
                        >
                          {asset.thumbnail_url ? (
                            <img 
                              src={asset.thumbnail_url} 
                              alt={asset.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className={`w-full h-full ${typeConfig.color} bg-opacity-10 flex items-center justify-center`}>
                              <Icon className={`w-8 h-8 ${typeConfig.color}`} />
                            </div>
                          )}
                          
                          {/* Overlay with name */}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                            <p className="text-white text-xs truncate">{asset.name}</p>
                          </div>
                          
                          {/* Duration badge */}
                          {asset.duration && (
                            <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-xs text-white">
                              {formatDuration(asset.duration)}
                            </div>
                          )}
                          
                          {/* Selection indicator */}
                          {selected && (
                            <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Upload Tab */
            <div className="flex-1 p-4">
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`h-full min-h-[300px] border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all ${
                  dragActive 
                    ? 'border-purple-500 bg-purple-500/10' 
                    : 'border-zinc-700 hover:border-zinc-500'
                }`}
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-12 h-12 text-purple-500 animate-spin mb-4" />
                    <p className="text-white font-medium">Uploading...</p>
                  </>
                ) : (
                  <>
                    <Upload className={`w-12 h-12 mb-4 ${dragActive ? 'text-purple-500' : 'text-zinc-500'}`} />
                    <p className="text-white font-medium mb-1">
                      {dragActive ? 'Drop file here' : 'Click or drag file to upload'}
                    </p>
                    <p className="text-zinc-500 text-sm">
                      Supported formats: Images, Videos, Audio
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={uploadAccept}
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-zinc-800">
          <p className="text-sm text-zinc-500">
            {selectedAssets.length > 0 
              ? `${selectedAssets.length} item${selectedAssets.length > 1 ? 's' : ''} selected`
              : 'No items selected'
            }
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={selectedAssets.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {multiple ? 'Add Selected' : 'Select'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * MediaPickerButton - A button that opens the MediaPicker modal
 * Convenience wrapper for common use cases
 */
export function MediaPickerButton({
  onSelect,
  mediaType = 'image',
  multiple = false,
  allowUpload = true,
  buttonText = 'Select from Library',
  buttonClassName = '',
  children
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={buttonClassName || 'flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-white transition-colors'}
      >
        {children || (
          <>
            <FolderOpen className="w-4 h-4" />
            {buttonText}
          </>
        )}
      </button>
      
      <MediaPickerModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSelect={onSelect}
        mediaType={mediaType}
        multiple={multiple}
        allowUpload={allowUpload}
        title={`Select ${MEDIA_TYPES[mediaType]?.label || 'Media'}`}
        uploadAccept={mediaType === 'image' ? 'image/*' : mediaType === 'video' ? 'video/*' : mediaType === 'audio' ? 'audio/*' : '*/*'}
      />
    </>
  );
}
